import {
  Attributes,
  Component,
  PropsWithRef,
  ReactNode,
  Suspense,
  createElement,
  lazy,
} from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { PageHeader } from './PageHeader';
import { LoadingRegion } from './LoadingFeedback';

class CodeBoundary extends Component<
  { children: () => ReactNode; fallback: (retry: () => void) => ReactNode; reset: () => void },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed
      ? this.props.fallback(() => {
          this.props.reset();
          this.setState({ failed: false });
        })
      : this.props.children();
  }
}

/** Retry only this module. Never reload the document or disturb drafts in other regions. */
export function deferred<P extends object>(
  loader: () => Promise<{ default: (props: P) => ReactNode }>,
  titleKey?: string
) {
  let Content = lazy(loader);
  function Deferred(props: P) {
    const { t } = useI18n();
    const planning = titleKey?.includes('Planning') || titleKey === 'nav.planCycles';
    const pending = (
      <div className="space-y-6">
        {titleKey && <PageHeader title={t(titleKey)} />}
        {planning && (
          <div className="w-full sm:w-72" aria-label={t('planning.cycle.label')}>
            <p className="form-label">{t('planning.cycle.label')}</p>
            <div
              aria-hidden="true"
              className="h-11 rounded-md border border-slate-300 bg-slate-100"
            />
          </div>
        )}
        <LoadingRegion shape={planning ? 'weeks' : 'list'} />
      </div>
    );
    return (
      <CodeBoundary
        reset={() => {
          Content = lazy(loader);
        }}
        fallback={retry => (
          <div role="alert" className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
            <p>{t('loading.codeFailed')}</p>
            <button type="button" className="btn-secondary" onClick={retry}>
              {t('cycles.retry')}
            </button>
          </div>
        )}
      >
        {() => (
          <Suspense fallback={pending}>
            {createElement(Content, props as Attributes & PropsWithRef<P>)}
          </Suspense>
        )}
      </CodeBoundary>
    );
  }
  return Deferred;
}
